import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Utensils, FileSignature, History, Landmark, Loader2, Settings2, X } from "lucide-react";
import { saveBlob } from "./bankUtils";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

import { BankFileModal } from "./BankFileModal";
import { BankHistoryModal } from "./BankHistoryModal";
import { MealListModal } from "./MealListModal";
import { PositionMealBulkModal } from "./PositionMealBulkModal";

const BankSettingsModal = ({ onClose }) => {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/payroll/bank-settings").then((r) => setF(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/payroll/bank-settings", f); toast.success("Banka ayarları kaydedildi"); onClose(); }
    catch { toast.error("Kaydedilemedi"); }
  };
  if (!f) return null;
  const inp = (k, label) => (
    <label className="text-[11px] font-bold text-slate-600 block">{label}
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`bank-setting-${k}`}
        className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm font-mono" />
    </label>
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="bank-settings-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm font-bold">DenizBank Talimat Ayarları</div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-2">
          {inp("firma_adi", "Firma Adı")}{inp("firma_iban", "Firma IBAN")}{inp("sube_kodu", "Şube Kodu")}
        </div>
        <div className="p-3 border-t flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">İptal</button>
          <button onClick={save} data-testid="bank-settings-save" className="px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm font-semibold">Kaydet</button>
        </div>
      </div>
    </div>
  );
};

export const BankFileButtons = ({ yil, ay, onReload }) => {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState(false);
  const closing = async () => {
    try {
      const r = await api.get(`/payroll/month-close-summary?yil=${yil}&ay=${ay}`, { responseType: "blob" });
      saveBlob(r, `ay_kapanis_${yil}_${ay}.pdf`);
      const miss = Number(r.headers["x-missing-receipts"] || 0);
      if (miss) toast.warning(`Ay kapanış özeti indirildi · ${miss} elden ödemenin imzalı makbuzu eksik`);
      else toast.success("Ay kapanış özeti indirildi");
    } catch { toast.error("Özet oluşturulamadı"); }
  };
  const refunds = async () => {
    try {
      const r = await api.get(`/payroll/refunds/pdf?yil=${yil}&ay=${ay}`, { responseType: "blob" });
      saveBlob(r, `iade_dokumu_${yil}_${ay}.pdf`);
      toast.success("Yemek & yol iade dökümü indirildi");
    } catch (e) { toast.error(e?.response?.status === 404 ? "Bu ay yemek/yol iadesi olan personel yok" : "Döküm oluşturulamadı"); }
  };
  const receipts = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/payroll/elden-receipts?yil=${yil}&ay=${ay}`, { responseType: "blob" });
      saveBlob(r, `elden_makbuz_${yil}_${ay}.pdf`);
      toast.success("Elden ödeme makbuzları indirildi");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Bu ay ELDEN işaretli ödeme yok" : "Makbuzlar oluşturulamadı");
    } finally { setBusy(false); }
  };
  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold";
  return (
    <>
      <button type="button" onClick={() => setOpen("maas")} data-testid="bank-file-maas" className={`${btn} bg-sky-700 hover:bg-sky-800 text-white`}>
        <Landmark size={13} /> Banka Talimatı (Maaş)
      </button>
      <button type="button" onClick={() => setOpen("prim")} data-testid="bank-file-prim" className={`${btn} bg-emerald-700 hover:bg-emerald-800 text-white`}>
        <Landmark size={13} /> Prim Talimatı
      </button>
      <button type="button" onClick={receipts} disabled={busy} data-testid="elden-receipts-btn" className={`${btn} bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />} Elden Makbuzları
      </button>
      <button type="button" onClick={() => setOpen("meal")} data-testid="meal-list-btn" className={`${btn} bg-teal-700 hover:bg-teal-800 text-white`}>
        <Utensils size={13} /> Yemek Kartı Listesi
      </button>
      {isAdmin && (
        <button type="button" onClick={() => setOpen("posmeal")} data-testid="position-meal-btn" className={`${btn} bg-white border border-teal-600 text-teal-700 hover:bg-teal-50`}>
          <Utensils size={13} /> Pozisyon Yemek Tutarları
        </button>
      )}
      <button type="button" onClick={closing} data-testid="month-close-btn" className={`${btn} bg-slate-900 hover:bg-slate-800 text-white`}>
        <ClipboardList size={13} /> Ay Kapanış Özeti
      </button>
      <button type="button" onClick={refunds} data-testid="refunds-pdf-btn" className={`${btn} bg-rose-700 hover:bg-rose-800 text-white`}>
        <FileSignature size={13} /> İade Dökümü PDF
      </button>
      <button type="button" onClick={() => setOpen("history")} data-testid="bank-history-open" className={`${btn} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}>
        <History size={13} /> Talimat Geçmişi
      </button>
      {isAdmin && (
        <button type="button" onClick={() => setOpen("settings")} data-testid="bank-settings-open" title="Banka talimat ayarları"
          className={`${btn} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}>
          <Settings2 size={13} />
        </button>
      )}
      {(open === "maas" || open === "prim") && <BankFileModal yil={yil} ay={ay} kind={open} onClose={() => setOpen("")} />}
      {open === "meal" && <MealListModal yil={yil} ay={ay} onClose={() => setOpen("")} />}
      {open === "posmeal" && <PositionMealBulkModal onClose={() => setOpen("")} onSaved={onReload} />}
      {open === "history" && <BankHistoryModal onClose={() => setOpen("")} />}
      {open === "settings" && <BankSettingsModal onClose={() => setOpen("")} />}
    </>
  );
};
